import { t } from 'utils/getTranslations';

export type MfaCodeErrorKind =
    | 'incomplete-code'
    | 'wrong-code'
    | 'expired-code'
    | 'session-expired'
    | 'too-many-attempts'
    | 'unknown';

/**
 * Sorts a Cognito TOTP failure into the state the verification screen shows. `EnableSoftwareTokenMFAException`
 * is what `verifySoftwareToken` answers for a wrong code during enrolment; `CodeMismatchException` is the same
 * answer from `sendMFACode` at sign-in.
 */
export function classifyMfaCodeError(err: unknown): MfaCodeErrorKind {
    const code = typeof err === 'object' && err !== null && 'code' in err ? String(err.code) : '';
    const message = err instanceof Error ? err.message.toLowerCase() : '';
    switch (code) {
        case 'CodeMismatchException':
        case 'EnableSoftwareTokenMFAException':
            return 'wrong-code';
        case 'ExpiredCodeException':
            return 'expired-code';
        case 'LimitExceededException':
        case 'TooManyRequestsException':
        case 'TooManyFailedAttemptsException':
            return 'too-many-attempts';
        case 'NotAuthorizedException':
            return isExpiredSession(message) ? 'session-expired' : 'wrong-code';
        default:
            return isExpiredSession(message) ? 'session-expired' : 'unknown';
    }
}

/**
 * Cognito words an expired sign-in two ways: the ~3-minute challenge session ("Invalid session for the
 * user, session is expired") and the 30-minute tokens behind enrolment ("Access Token has expired").
 */
function isExpiredSession(message: string): boolean {
    return message.includes('session') || message.includes('expired');
}

export function describeMfaCodeError(kind: MfaCodeErrorKind): string {
    switch (kind) {
        case 'incomplete-code':
            return t('auth.mfa-code-length');
        case 'wrong-code':
            return t('auth.mfa-code-wrong');
        case 'expired-code':
            return t('auth.mfa-code-expired');
        case 'session-expired':
            return t('auth.mfa-session-expired');
        case 'too-many-attempts':
            return t('auth.mfa-too-many-attempts');
        default:
            return t('auth.mfa-verification-error');
    }
}

/** Groups a Base32 setup key in fours, the way authenticator apps print it. */
export function formatSetupKey(secret: string): string {
    return (secret.match(/.{1,4}/g) ?? []).join(' ');
}

export function buildOtpAuthUrl(email: string, secret: string): string {
    const label = encodeURIComponent(`Crespect:${email}`);
    return `otpauth://totp/${label}?secret=${secret}&issuer=Crespect&algorithm=SHA1&digits=6&period=30`;
}
